import { AccessCheckElementAccess } from '../constants/accessCheckElementAccess';
import { DEFAULT_HIDE_ON_LOADING } from '../constants/defaultHideOnLoading';
import I18n from 'I18n';
import { generateResourceKey } from './generateResourceKey';
export function determineVisibility({
  isDataLoading,
  visibilityCheck,
  minimumResourceInfoForVisibility,
  hideOnLoading = DEFAULT_HIDE_ON_LOADING
}) {
  if (isDataLoading) {
    // If the data is loading and hideOnLoading is true, return that isVisibility is false.
    return !hideOnLoading;
  }
  if (!minimumResourceInfoForVisibility || minimumResourceInfoForVisibility.resource.type === '' || minimumResourceInfoForVisibility.action.name === '') {
    return true;
  }
  return visibilityCheck();
}
export function determineStatus(hasAccess, isVisible, isLoading) {
  if (isLoading) {
    return AccessCheckElementAccess.LOADING;
  }
  if (hasAccess) {
    return AccessCheckElementAccess.HAS_ACCESS;
  }
  if (isVisible) {
    return AccessCheckElementAccess.NO_ACCESS_VISIBLE;
  }
  return AccessCheckElementAccess.NO_ACCESS_HIDDEN;
}
export function getMissingSeatName(seat) {
  return I18n.text('access-check-lib.accessCheck.messaging.missingSeat', {
    seatName: seat
  });
}
function checkVisibilityFromMinimumResourceInfo({
  resourcesRequiredForVisibility,
  accessCheckEvals,
  hideOnLoading,
  isLoading,
  hasAccess
}) {
  // Check each minimum visibility requirement
  return resourcesRequiredForVisibility.every(minimumResourceInfo => {
    var _minimumResult$hasAcc;
    const key = generateResourceKey(minimumResourceInfo.resource, minimumResourceInfo.action);
    const minimumResult = accessCheckEvals.get(key);
    const minimumHasAccess = (_minimumResult$hasAcc = minimumResult === null || minimumResult === void 0 ? void 0 : minimumResult.hasAccess) !== null && _minimumResult$hasAcc !== void 0 ? _minimumResult$hasAcc : false;

    // Component is visible only if user has access to primary resources OR minimum requirements for visibility, otherwise its hidden.
    // If its loading, visibility is determined by hideOnLoading flag
    return determineVisibility({
      minimumResourceInfoForVisibility: minimumResourceInfo,
      hideOnLoading,
      isDataLoading: isLoading,
      visibilityCheck: () => hasAccess || minimumHasAccess
    });
  });
}
export function buildNoScopeAccessInfo(hasAccess, isVisible) {
  const status = determineStatus(hasAccess, isVisible, false);
  return {
    hasAccess,
    isVisible,
    missingSeats: [],
    missingPermissions: [],
    missingScopes: [],
    requireAllPermissions: false,
    status,
    isLoading: false
  };
}
export function buildAccessInfoFromAccessCheckEvaluations({
  resourcesToCheck,
  resourcesRequiredForVisibility,
  accessCheckEvals,
  hideOnLoading,
  isLoading
}) {
  // Generate unique keys for each primary resource to check
  const primaryKeys = resourcesToCheck.map(resourceInfo => generateResourceKey(resourceInfo.resource, resourceInfo.action));

  // User has access only if they have access to ALL primary resources
  const failedAccessCheckKeys = primaryKeys.filter(key => {
    const result = accessCheckEvals.get(key);
    return !(result !== null && result !== void 0 && result.hasAccess);
  });
  const hasAccess = failedAccessCheckKeys.length === 0;

  // Collect all missing permissions and seats across resources
  const missingPermissions = new Set();
  const missingSeats = new Set();
  let requireAllPermissions = false;

  // If access is denied, aggregate all missing requirements for display
  failedAccessCheckKeys.forEach(key => {
    const result = accessCheckEvals.get(key);
    if (result && !result.hasAccess && result.data) {
      // Collect missing seat requirements
      if (result.data.lowestSeatRequirement) {
        const seatName = getMissingSeatName(result.data.lowestSeatRequirement);
        missingSeats.add(seatName);
      }
      // Collect missing permissions (deduplicated)
      if (result.data.requiredPermissions) {
        result.data.requiredPermissions.forEach(perm => {
          missingPermissions.add(perm);
        });
        // Track if any resource requires ALL permissions vs. ANY permission
        requireAllPermissions = requireAllPermissions || result.data.requiresAllPermissions;
      }
    }
  });

  // Determine initial visibility based on minimum visibility requirements if they exist, otherwise default based on loading state and hideOnLoading flag
  const hasVisibilityScopesDefined = (resourcesRequiredForVisibility === null || resourcesRequiredForVisibility === void 0 ? void 0 : resourcesRequiredForVisibility.length) > 0;
  const isVisible = hasVisibilityScopesDefined ? checkVisibilityFromMinimumResourceInfo({
    resourcesRequiredForVisibility,
    accessCheckEvals,
    hideOnLoading,
    isLoading,
    hasAccess
  }) : determineVisibility({
    hideOnLoading,
    isDataLoading: isLoading,
    visibilityCheck: () => true
  });

  // Calculate final status based on access, visibility, and loading state
  const status = determineStatus(hasAccess, isVisible, isLoading);

  // Return aggregated access information
  return {
    hasAccess,
    missingPermissions: Array.from(missingPermissions),
    missingScopes: [],
    missingSeats: Array.from(missingSeats),
    requireAllPermissions,
    isVisible,
    status,
    isLoading
  };
}